import { format, isValid } from "date-fns";
import { Edit, FileText, Search, Terminal, Wrench as ToolIcon } from "lucide-react";
import React from "react";
import { cn } from "@/lib/utils";
import { AgentMessage } from "@/models/agent/message";

interface Props {
  message: AgentMessage;
  className?: string;
  onToolClick?: (message: AgentMessage) => void;
}

// ツール名のアイコンマッピング
const TOOL_ICONS: Record<string, React.ComponentType<{ className?: string; }>> = {
  create_file: FileText,
  str_replace: Edit,
  web_search: Search,
  execute_command: Terminal,
  read_file: FileText,
  write_file: FileText,
  list_files: FileText,
};

// ツール名の日本語マッピング
const TOOL_LABELS: Record<string, string> = {
  create_file: "ファイル作成",
  str_replace: "テキスト編集",
  web_search: "Web検索",
  execute_command: "コマンド実行",
  read_file: "ファイル読み込み",
  write_file: "ファイル書き込み",
  list_files: "ファイル一覧",
};

const ToolCallMessage: React.FC<Props> = ({
  message,
  className,
  onToolClick,
}) => {
  // contentからツール情報を抽出
  let toolName = "";

  if (typeof message.content === "object" && message.content?.name) {
    toolName = String(message.content.name);
  }

  const IconComponent = TOOL_ICONS[toolName] || ToolIcon;
  const toolLabel = TOOL_LABELS[toolName] || toolName;

  const handleClick = () => {
    if (onToolClick) {
      onToolClick(message);
    }
  };

  return (
    <div className={cn("flex justify-start my-3", className)}>
      <div className="max-w-[70%]">
        <button
          onClick={handleClick}
          className={cn(
            "group flex items-center space-x-2 px-2 py-1.5 border",
            "rounded-lg transition-all duration-200 cursor-pointer",
            message.isSubAgentCall
              ? "bg-[#eaf0f7] border-[#cad6ee] hover:bg-[#e2ebf6]"
              : "bg-gray-100 border-gray-300 hover:bg-gray-200",
          )}
        >
          {/* ツールアイコン */}
          <div
            className={cn(
              "flex-shrink-0 w-6 h-6 rounded-[8px] flex items-center justify-center",
              message.isSubAgentCall ? "bg-[#d1ddeb]" : "bg-[#d1d5db]",
            )}
          >
            <IconComponent
              className={cn(
                "w-[14px] h-[14px] text-white",
                message.isSubAgentCall ? "text-[#687f99]" : "text-[#4a5565]",
              )}
            />
          </div>

          {/* ツール情報 */}
          <div className="flex-1 text-left">
            <div className={cn("font-medium text-xs", message.isSubAgentCall ? "text-[#4e4e4e]" : "text-gray-900")}>
              {toolLabel}
            </div>
          </div>

          {/* 展開アイコン */}
          <div className="flex-shrink-0">
            <div
              className={cn(
                "w-5 h-5 rounded-full flex items-center justify-center transition-colors",
                message.isSubAgentCall ? "bg-[#d1ddeb]" : "bg-[#d1d5db]",
              )}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke={message.isSubAgentCall ? "#687f99" : "#4a5565"}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </button>

        {/* タイムスタンプ */}
        <div className="mt-1 text-xs text-gray-400 px-1">
          {isValid(message.createdAt) && format(message.createdAt, "yyyy/M/d H:mm")}
        </div>
      </div>
    </div>
  );
};

export default ToolCallMessage;
